import { captureException, addExceptionStep } from '@/lib/errorTracking';
import { useCallback, useMemo, useRef } from 'react';
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { usePostHog } from '@posthog/react';
import { isSessionExpiredError } from '@/auth/sessionExpiry';
import {
  handleDeleteGeometryGeometriesGeometryIdDeleteMutation,
  handleDownloadMeshMeshesMeshIdDownloadGetOptions,
  handleListGeometriesGeometriesGetQueryKey,
  handleListGeometryMeshesGeometriesGeometryIdMeshesGetOptions,
} from '../api/generated/@tanstack/react-query.gen';
import { handleListGeometriesGeometriesGet } from '../api/generated';
import type {
  AiActivitySummary,
  GeometryResponse,
  ListGeometriesResponse,
  MeshFileType,
  MeshMode,
  MeshStatus,
  Visibility,
} from '../api/generated/types.gen';
import { triggerDownload } from '../utils/triggerDownload';
import { formatTimestamp } from '../utils/formatTimestamp';
import { useStore } from '../stores/useStore';
import { TIMEOUTS } from '../constants';
import { logger } from '@/lib/logger';

export type DashboardGeometryStatus =
  | 'processing'
  | 'error'
  | 'no_mesh'
  | 'meshing'
  | 'done'
  | 'failed';
export type DashboardDownloadType = 'msh' | 'fluent' | 'gmsh' | 'openfoam';

export interface DashboardGeometry {
  projectId: string;
  fileName: string;
  status: DashboardGeometryStatus;
  durationLabel: string;
  completedLabel: string | null;
  canDownload: boolean;
  latestMeshId: string | null;
  visibility: Visibility;
  updatedAt: string | null;
  /** Diagnosis-completion timestamp; the stable thumbnail cache-version token. */
  diagnosisAt: string | null;
  elementType: 'tet' | 'hex' | 'poly' | null;
  /**
   * Latest mesh's domain mode (ENG-2772). Prefers the compact list-row
   * `domain_mode` (list/lineage strip full `mesh_params` — ENG-742); falls
   * back to `mesh_params.domain.mode` on detail responses. Null when there
   * is no latest mesh, or the mesh has no mode (legacy upload).
   */
  meshMode: MeshMode | null;
  /** Import-time geometry-prep intent (ENG-1245); null on legacy rows. */
  geoPrep: 'repair' | 'ftm' | null;
  /**
   * What the AI retry loop has been doing under this lineage's meshes
   * (ENG-2016), rolled up server-side so the card's chip and the "AI activity"
   * tab count cost no extra request. Null on an endpoint that does not compute
   * it — "not computed", never "nothing happened".
   */
  aiActivity: AiActivitySummary | null;
}

const DOWNLOAD_TYPE_TO_FILE_TYPE: Record<DashboardDownloadType, MeshFileType> =
  {
    fluent: 'fluent',
    msh: 'mesh',
    gmsh: 'gmsh',
    openfoam: 'openfoam',
  };

const MESH_STATUS_TO_DASHBOARD: Record<MeshStatus, DashboardGeometryStatus> = {
  pending: 'meshing',
  meshing: 'meshing',
  recombining: 'meshing',
  completed: 'done',
  failed: 'failed',
  cancelled: 'failed',
};

export function mapGeometryToRow(resp: GeometryResponse): DashboardGeometry {
  return {
    projectId: resp.id,
    fileName: resp.filename ?? 'Untitled',
    status:
      resp.status === 'diagnosing' || resp.status === 'repairing'
        ? 'processing'
        : resp.status === 'error'
          ? 'error'
          : 'no_mesh',
    durationLabel: formatTimestamp(resp.created_at),
    // Completed is a MESH timestamp; a bare geometry row has completed
    // nothing. The meshMap merge below fills it from the latest mesh's
    // completed_at. It must NOT fall back to updated_at: that column moved
    // whenever the row was touched (visibility toggle, rename) while
    // claiming to be a completion time (ENG-595).
    completedLabel: null,
    canDownload: false,
    latestMeshId: null,
    visibility: resp.visibility,
    updatedAt: resp.updated_at,
    diagnosisAt: resp.diagnosis_at ?? null,
    elementType: null,
    meshMode: null,
    geoPrep: resp.geo_prep ?? null,
    aiActivity: resp.ai_activity ?? null,
  };
}

/**
 * Default page size for the geometries list. Owned here so every consumer of
 * `listGeometriesInfiniteOptions` (hook, components, route loaders) builds
 * the same query key — diverging limits would split the cache entry.
 */
export const GEOMETRIES_PAGE_LIMIT = 20;

/**
 * Infinite-query options for the cursor-paginated `GET /geometries` list.
 * Single source of truth shared by `useDashboardGeometries`, the SimOps
 * study layout, and the `/meshgen` + `/simops/$studyId` route loaders so
 * they all read and warm the same cache entry.
 */
export function listGeometriesInfiniteOptions(limit = GEOMETRIES_PAGE_LIMIT) {
  return infiniteQueryOptions({
    queryKey: handleListGeometriesGeometriesGetQueryKey({ query: { limit } }),
    queryFn: async ({ pageParam, signal }): Promise<ListGeometriesResponse> => {
      const { data } = await handleListGeometriesGeometriesGet({
        query: {
          cursor: pageParam ?? undefined,
          limit,
        },
        signal,
        throwOnError: true,
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: null as string | null,
  });
}

// ---------------------------------------------------------------------------
// Retry-storm guard for the polled GET /geometries list
//
// The dashboard list combines three refetch triggers: the ~5s poll below,
// TanStack's default `refetchOnWindowFocus`, and its default 3× retry. On a
// backend 5xx they multiply — every poll tick (and every window focus) fans
// out into a burst of failing requests. The backend fix removes the root
// cause; this keeps a transient hiccup from hammering the API in the meantime.
// ---------------------------------------------------------------------------

/** Up to this many retries for transient network errors (errors with no HTTP
 *  status). Server errors (5xx) are never retried — see `shouldRetryListQuery`. */
const MAX_TRANSIENT_RETRIES = 2;
const RETRY_BACKOFF_BASE_MS = 1_000;
const RETRY_BACKOFF_CAP_MS = 30_000;
/** While the list is in an error state the fast poll is paused in favour of
 *  this backed-off, jittered cadence — slow enough not to hammer, frequent
 *  enough that the dashboard still self-heals once the backend recovers. */
const ERROR_BACKOFF_INTERVAL_MS = 30_000;
const JITTER_RATIO = 0.25;

/** ±`JITTER_RATIO` jitter so a fleet of tabs recovering from the same outage
 *  don't re-sync into a thundering herd. */
export function jitter(ms: number): number {
  return Math.round(ms * (1 - JITTER_RATIO + Math.random() * JITTER_RATIO * 2));
}

/** True for an HTTP 5xx. The generated client throws the response error with a
 *  numeric `status` (some transports nest it under `response.status`); a
 *  network error has neither, so it reads as not-a-server-error. */
export function isServerError(error: unknown): boolean {
  const e = error as
    | { status?: number; response?: { status?: number } }
    | null
    | undefined;
  const code = e?.status ?? e?.response?.status;
  return code != null && code >= 500;
}

/** Never retry a 5xx (the poll recovers it); allow a small bounded retry for
 *  transient network errors only. */
export function shouldRetryListQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (isSessionExpiredError(error)) return false;
  if (isServerError(error)) return false;
  return failureCount < MAX_TRANSIENT_RETRIES;
}

/** Capped, jittered exponential backoff for the retries we do allow. */
export function listRetryDelay(attemptIndex: number): number {
  const base = Math.min(
    RETRY_BACKOFF_CAP_MS,
    RETRY_BACKOFF_BASE_MS * 2 ** attemptIndex,
  );
  return jitter(base);
}

/** Poll cadence: normal while healthy, paused down to a jittered backoff while
 *  the list query's last fetch errored. `hasError` is `query.state.error !=
 *  null`, which (unlike `status`) stays true when a background refetch fails
 *  with stale data still in cache — the common 502-mid-session case. */
export function listRefetchInterval(hasError: boolean): number {
  return hasError
    ? jitter(ERROR_BACKOFF_INTERVAL_MS)
    : TIMEOUTS.DASHBOARD_POLL_INTERVAL;
}

/**
 * Hook for managing dashboard geometries.
 * Uses TanStack Query infinite query for cursor-based pagination of GET /geometries.
 */
export function useDashboardGeometries(limit = GEOMETRIES_PAGE_LIMIT) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const showToast = useStore((s) => s.showToast);

  const {
    data,
    isLoading,
    error,
    dataUpdatedAt,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...listGeometriesInfiniteOptions(limit),
    retry: shouldRetryListQuery,
    retryDelay: listRetryDelay,
    refetchInterval: (query) => listRefetchInterval(query.state.error != null),
  });

  const baseGeometries: DashboardGeometry[] = useMemo(
    () =>
      (data?.pages ?? []).flatMap((page) => page.items.map(mapGeometryToRow)),
    [data],
  );

  // Fetch latest mesh per geometry to derive real status + canDownload.
  // Each query is keyed by geometry_id so results stay aligned.
  //
  // Include 'error' (failed diagnosis) geometries here too — users can
  // and do kick off mesh jobs from failed-diagnosis geometries, and the
  // dashboard should reflect the meshing pipeline rather than freezing
  // on the stale diagnosis-failed badge. When a mesh exists for an
  // 'error' geometry, the meshMap merge below promotes the row's
  // status to meshing/done/failed accordingly.
  const idleGeoIds = useMemo(
    () =>
      baseGeometries
        .filter((g) => g.status === 'no_mesh' || g.status === 'error')
        .map((g) => g.projectId),
    [baseGeometries],
  );

  const meshQueries = useQueries({
    queries: idleGeoIds.map((geoId) => ({
      ...handleListGeometryMeshesGeometriesGeometryIdMeshesGetOptions({
        path: { geometry_id: geoId },
        // `lineage: true` walks the whole version chain — meshes built
        // against an auto-repaired v2 / v3 are attached to that child
        // geometry id, not the root, so without this the query returns
        // 0 items and the row stays on the 'error' badge even when a
        // mesh job is in flight (which is what the workspace shows).
        // Matches the SimOps picker's query for the same reason.
        query: { limit: 1, lineage: true },
      }),
      staleTime: 30_000,
    })),
  });

  // Stabilize dependency: only recompute when actual mesh data changes,
  // not when useQueries returns a new array reference.
  const meshDataKey = meshQueries
    .map((q) => {
      const m = q.data?.items?.[0];
      // Prefer compact list-row fields; mesh_params is stripped on lineage
      // lists (ENG-742) so domain.mode alone would never re-merge.
      const mode = m?.domain_mode ?? m?.mesh_params?.domain?.mode ?? '';
      const element = m?.element_type ?? m?.mesh_params?.element_type ?? '';
      return m
        ? `${m.id}:${m.status}:${m.completed_at ?? ''}:${mode}:${element}`
        : '';
    })
    .join(',');

  const geometries: DashboardGeometry[] = useMemo(() => {
    const meshMap = new Map<
      string,
      {
        status: DashboardGeometryStatus;
        meshId: string;
        elementType: 'tet' | 'hex' | 'poly' | null;
        meshMode: MeshMode | null;
        completedLabel: string | null;
      }
    >();
    meshQueries.forEach((q, i) => {
      const geoId = idleGeoIds[i];
      if (!geoId || !q.data) return;
      const latestMesh = q.data.items?.[0];
      if (latestMesh) {
        meshMap.set(geoId, {
          status: MESH_STATUS_TO_DASHBOARD[latestMesh.status],
          meshId: latestMesh.id,
          elementType:
            latestMesh.element_type ??
            latestMesh.mesh_params?.element_type ??
            null,
          meshMode:
            latestMesh.domain_mode ??
            latestMesh.mesh_params?.domain?.mode ??
            null,
          // Real completion time. Null while the mesh is running, and null
          // forever for meshes made before the backend stamped completed_at
          // (ENG-595) — those render as an em dash rather than a made-up time.
          completedLabel: latestMesh.completed_at
            ? formatTimestamp(latestMesh.completed_at)
            : null,
        });
      }
    });

    return baseGeometries.map((g) => {
      const meshInfo = meshMap.get(g.projectId);
      if (!meshInfo) return g;
      return {
        ...g,
        status: meshInfo.status,
        canDownload: meshInfo.status === 'done',
        latestMeshId: meshInfo.meshId,
        elementType: meshInfo.elementType,
        meshMode: meshInfo.meshMode,
        completedLabel: meshInfo.completedLabel,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseGeometries, idleGeoIds, meshDataKey]);

  const lastUpdatedAt = dataUpdatedAt
    ? new Date(dataUpdatedAt).toISOString()
    : null;

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: handleListGeometriesGeometriesGetQueryKey({ query: { limit } }),
    });
  }, [queryClient, limit]);

  // Delete geometry (soft delete)
  const deleteMutation = useMutation({
    ...handleDeleteGeometryGeometriesGeometryIdDeleteMutation(),
    onSuccess: (_data, variables) => {
      posthog.capture('geometry_deleted', {
        geometry_id: variables.path.geometry_id,
      });
      addExceptionStep(`Geometry deleted: ${variables.path.geometry_id}`, {
        category: 'geometry',
      });
      refresh();
    },
    onError: (error, variables) => {
      captureException(error, {
        geometryId: variables.path.geometry_id,
        action: 'delete_geometry',
      });
      showToast('Failed to delete geometry');
    },
  });

  const deleteGeometry = useCallback(
    async (projectId: string) => {
      await deleteMutation.mutateAsync({
        path: { geometry_id: projectId },
      });
    },
    [deleteMutation],
  );

  // Ref avoids putting geometries in callback deps, preventing cascading re-renders
  const geometriesRef = useRef(geometries);
  geometriesRef.current = geometries;

  const downloadGeometry = useCallback(
    async (
      projectId: string,
      _fallbackName?: string,
      downloadType: DashboardDownloadType = 'fluent',
    ) => {
      const geo = geometriesRef.current.find((g) => g.projectId === projectId);
      const meshId = geo?.latestMeshId;
      if (!meshId) {
        logger.warn('[useDashboardGeometries] no mesh to download');
        return;
      }
      try {
        const data = await queryClient.fetchQuery(
          handleDownloadMeshMeshesMeshIdDownloadGetOptions({
            path: { mesh_id: meshId },
            query: { file: DOWNLOAD_TYPE_TO_FILE_TYPE[downloadType] },
          }),
        );
        triggerDownload(data.download_url);
        posthog.capture('mesh_exported_from_dashboard', {
          geometry_id: projectId,
          file_type: DOWNLOAD_TYPE_TO_FILE_TYPE[downloadType],
        });
      } catch (error) {
        logger.error('[useDashboardGeometries] download failed', { error });
        captureException(error, {
          projectId,
          meshId,
          downloadType,
          action: 'dashboard_download',
        });
      }
    },
    [queryClient, posthog],
  );

  const shareGeometry = useCallback(async (_projectId: string) => {}, []);

  const isSharingProject = useCallback((_projectId: string) => false, []);
  const isDeletingProject = useCallback(
    (projectId: string) =>
      deleteMutation.isPending &&
      deleteMutation.variables?.path?.geometry_id === projectId,
    [deleteMutation.isPending, deleteMutation.variables],
  );
  return {
    geometries,
    isLoading,
    error:
      error instanceof Error
        ? error.message
        : error != null
          ? 'Failed to load geometries'
          : null,
    lastUpdatedAt,
    refresh,
    deleteGeometry,
    downloadGeometry,
    shareGeometry,
    isSharingProject,
    isDeletingProject,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
